package studio.goodegg.capsule.db

import app.cash.sqldelight.Query
import app.cash.sqldelight.SuspendingTransacterImpl
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import kotlin.Any
import kotlin.Boolean
import kotlin.Long
import kotlin.String

public class SubscriptionQueries(
  driver: SqlDriver,
) : SuspendingTransacterImpl(driver) {
  public fun <T : Any> selectAll(mapper: (
    slug: String,
    feedurl: String,
    title: String,
    artist: String,
    image: String?,
    notify: Boolean,
    collection: String?,
    autodownload: Boolean,
    updated_at: Long,
    deleted: Boolean,
  ) -> T): Query<T> = Query(-1_273_474_211, arrayOf("subscription"), driver, "subscription.sq",
      "selectAll",
      "SELECT subscription.slug, subscription.feedurl, subscription.title, subscription.artist, subscription.image, subscription.notify, subscription.collection, subscription.autodownload, subscription.updated_at, subscription.deleted FROM subscription WHERE deleted = 0") {
      cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4),
      cursor.getBoolean(5)!!,
      cursor.getString(6),
      cursor.getBoolean(7)!!,
      cursor.getLong(8)!!,
      cursor.getBoolean(9)!!
    )
  }

  public fun selectAll(): Query<Subscription> = selectAll { slug, feedurl, title, artist, image,
      notify, collection, autodownload, updated_at, deleted ->
    Subscription(
      slug,
      feedurl,
      title,
      artist,
      image,
      notify,
      collection,
      autodownload,
      updated_at,
      deleted
    )
  }

  public fun <T : Any> selectAllIncludingDeleted(mapper: (
    slug: String,
    feedurl: String,
    title: String,
    artist: String,
    image: String?,
    notify: Boolean,
    collection: String?,
    autodownload: Boolean,
    updated_at: Long,
    deleted: Boolean,
  ) -> T): Query<T> = Query(-335_744_399, arrayOf("subscription"), driver, "subscription.sq",
      "selectAllIncludingDeleted",
      "SELECT subscription.slug, subscription.feedurl, subscription.title, subscription.artist, subscription.image, subscription.notify, subscription.collection, subscription.autodownload, subscription.updated_at, subscription.deleted FROM subscription") {
      cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4),
      cursor.getBoolean(5)!!,
      cursor.getString(6),
      cursor.getBoolean(7)!!,
      cursor.getLong(8)!!,
      cursor.getBoolean(9)!!
    )
  }

  public fun selectAllIncludingDeleted(): Query<Subscription> = selectAllIncludingDeleted { slug,
      feedurl, title, artist, image, notify, collection, autodownload, updated_at, deleted ->
    Subscription(
      slug,
      feedurl,
      title,
      artist,
      image,
      notify,
      collection,
      autodownload,
      updated_at,
      deleted
    )
  }

  public fun <T : Any> selectSubscription(slug: String, mapper: (
    slug: String,
    feedurl: String,
    title: String,
    artist: String,
    image: String?,
    notify: Boolean,
    collection: String?,
    autodownload: Boolean,
    updated_at: Long,
    deleted: Boolean,
  ) -> T): Query<T> = SelectSubscriptionQuery(slug) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4),
      cursor.getBoolean(5)!!,
      cursor.getString(6),
      cursor.getBoolean(7)!!,
      cursor.getLong(8)!!,
      cursor.getBoolean(9)!!
    )
  }

  public fun selectSubscription(slug: String): Query<Subscription> = selectSubscription(slug) {
      slug_, feedurl, title, artist, image, notify, collection, autodownload, updated_at, deleted ->
    Subscription(
      slug_,
      feedurl,
      title,
      artist,
      image,
      notify,
      collection,
      autodownload,
      updated_at,
      deleted
    )
  }

  public suspend fun insert(
    slug: String,
    feedurl: String,
    title: String,
    artist: String,
    image: String?,
    notify: Boolean,
    collection: String?,
    autodownload: Boolean,
    updated_at: Long,
    deleted: Boolean,
  ) {
    driver.execute(325_249_377, """
        |INSERT INTO subscription (slug, feedurl, title, artist, image, notify, collection, autodownload, updated_at, deleted)
        |VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        |ON CONFLICT(slug) DO UPDATE SET
        |  feedurl = excluded.feedurl,
        |  title = excluded.title,
        |  artist = excluded.artist,
        |  image = excluded.image,
        |  notify = excluded.notify,
        |  collection = excluded.collection,
        |  autodownload = excluded.autodownload,
        |  updated_at = excluded.updated_at,
        |  deleted = excluded.deleted
        """.trimMargin(), 10) {
          bindString(0, slug)
          bindString(1, feedurl)
          bindString(2, title)
          bindString(3, artist)
          bindString(4, image)
          bindBoolean(5, notify)
          bindString(6, collection)
          bindBoolean(7, autodownload)
          bindLong(8, updated_at)
          bindBoolean(9, deleted)
        }.await()
    notifyQueries(325_249_377) { emit ->
      emit("subscription")
    }
  }

  public suspend fun softDelete(updated_at: Long, slug: String) {
    driver.execute(237_941_021, """
        |UPDATE subscription
        |SET deleted = 1, updated_at = ?
        |WHERE slug = ?
        """.trimMargin(), 2) {
          bindLong(0, updated_at)
          bindString(1, slug)
        }.await()
    notifyQueries(237_941_021) { emit ->
      emit("subscription")
    }
  }

  public suspend fun delete(slug: String) {
    driver.execute(173_583_443, """DELETE FROM subscription WHERE slug = ?""", 1) {
          bindString(0, slug)
        }.await()
    notifyQueries(173_583_443) { emit ->
      emit("subscription")
    }
  }

  private inner class SelectSubscriptionQuery<out T : Any>(
    public val slug: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("subscription", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("subscription", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_070_772_961,
        """SELECT subscription.slug, subscription.feedurl, subscription.title, subscription.artist, subscription.image, subscription.notify, subscription.collection, subscription.autodownload, subscription.updated_at, subscription.deleted FROM subscription WHERE deleted = 0 AND slug = ?""",
        mapper, 1) {
      bindString(0, slug)
    }

    override fun toString(): String = "subscription.sq:selectSubscription"
  }
}
